import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { HttpError } from '@/api/client';
import { getCheck } from '@/api/checks';
import { ErrorScreen, InvalidCodeScreen, LoadingScreen } from '@/features/check/StatusScreen';
import { clearSession, loadSession } from '@/session/storage';
import { applyTheme, assertReadableTheme } from '@/theme/skin';
import type { CheckoutContext } from './checkoutContext';

/**
 * Everything the checkout steps share: the session, the check, the brand, and every way those can fail.
 *
 * A layout route rather than the same hook called from every step. The steps are separate routes so the browser's back
 * button behaves - a guest will press it, and on a phone it is often the only control they trust - and each
 * of them needs the same session read, the same query, the same theme application and the same handling for
 * an expired token. Loading the check once here also means moving between tip and payment does not refetch
 * it, and cannot show two different totals on two screens.
 *
 * Children read `useCheckout()` and can assume both are present; nothing renders past this point until they
 * are.
 */
export function CheckoutLayout() {
    // Read once per mount. A session that expires mid-view surfaces as a 401 from the API, which is handled
    // below; re-reading storage on every render would just churn.
    const session = useMemo(() => loadSession(), []);
    const location = session?.location;

    useEffect(() => {
        if (!location) return;

        applyTheme(location.theme);
        assertReadableTheme(location.theme, location.name);
    }, [location]);

    const check = useQuery({
        queryKey: ['check', session?.location.id],
        queryFn: ({ signal }) => getCheck(session!.accessToken, signal),
        enabled: !!session,
        // A guest is looking at a live check that a server may still be adding to. Refetching when the tab
        // regains focus is exactly wrong during a gateway redirect, though, which is why the client disables
        // that globally.
        staleTime: 15_000,
        retry: (failureCount, error) => {
            // Retrying an expired session just burns time before showing the guest the same thing.
            if (error instanceof HttpError && (error.status === 401 || error.status === 404)) return false;
            return failureCount < 2;
        },
    });

    // No session at all: they reached this route without scanning, or the token aged out while the tab sat
    // open. Either way the printed QR code is the way back in.
    if (!session) return <InvalidCodeScreen />;

    if (check.isPending) return <LoadingScreen />;

    if (check.isError) {
        const status = check.error instanceof HttpError ? check.error.status : undefined;

        if (status === 401) {
            // The token is gone or rejected; drop it so a rescan starts clean rather than reusing it.
            clearSession();
            return <ErrorScreen titleKey="error.expired.title" detailKey="error.expired.detail" />;
        }

        if (status === 404) return <InvalidCodeScreen />;

        return (
            <ErrorScreen
                titleKey="error.checkLoad.title"
                detailKey="error.unreachable.detail"
                onRetry={() => check.refetch()}
            />
        );
    }

    return (
        <Outlet
            context={
                {
                    location: session.location,
                    check: check.data,
                    accessToken: session.accessToken,
                } satisfies CheckoutContext
            }
        />
    );
}
